import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface GradeDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
}

const GradeDropdown: React.FC<GradeDropdownProps> = ({
  value,
  onChange = () => {},
  placeholder = "Select Grade",
  disabled = false,
  onDropdownLoad
}) => {
  const [gradeOptions, setGradeOptions] = useState<{key: string, label: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const onDropdownLoadRef = useRef(onDropdownLoad);

  useEffect(() => {
    onDropdownLoadRef.current = onDropdownLoad;
  }, [onDropdownLoad]);

  const loadGradeOptions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const grades = await FiltersService.getDropdownItems(DROPDOWN_CODES.Grade);
      
      if (!grades || grades.length === 0) {
        setError('No grade options available');
        message.warning('No grade options available from the server');
      } else {
        const allOption = { key: '', label: 'Select Grade' };
        const mappedOptions = [allOption, ...grades.map(item => ({
          key: (item.value || '').trim().toUpperCase(),
          label: item.label || item.value || ''
        }))];
        setGradeOptions(mappedOptions);
      }
      
      onDropdownLoadRef.current?.();
    } catch (error) {
      console.error('Failed to load grade options:', error);
      setError('Failed to load grade options');
      message.error('Failed to load grade options. Please try again.');
      onDropdownLoadRef.current?.();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGradeOptions();
  }, [loadGradeOptions]);

  const handleGradeChange = (gradeCode: string) => {
    onChange(gradeCode);
  };

  // DEBUG LOG for dropdown value debugging
  console.log('GradeDropdown value:', value, 'options:', gradeOptions, 'loading:', loading, 'error:', error);

  return (
    <div>
      <SimpleDropdown
        options={gradeOptions}
        value={value}
        onChange={handleGradeChange}
        placeholder={loading ? "Loading grades..." : error ? "No options available" : placeholder}
        disabled={disabled || loading || !!error}
      />
      {error && (
        <div className="text-danger small mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default GradeDropdown; 